import { useEffect, useMemo, useState } from "react";
import { Sparkles, Loader2, Printer, Download, FileText, Clock, Building2, CalendarDays, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell,
  ComposedChart, Line, LabelList,
} from "recharts";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { fetchMonthlyReport, fetchTrends, exportCsvUrl } from "@/lib/api";
import logo from "@/assets/ssg-logo.jpeg";

const FR_MONTHS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];

const CHART_COLORS = ["#0A192F", "#D4AF37", "#1E5F8C", "#2E7D6F", "#B4472E", "#6B4FA0", "#C59B27", "#3E7CB1"];
const MONTH_COLORS = ["#3E7CB1", "#2E7D6F", "#6B4FA0", "#C59B27", "#B4472E", "#0A192F"];

function monthOptions() {
  const opts = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = `${FR_MONTHS[d.getMonth()]} ${d.getFullYear()}`;
    opts.push({ value, label });
  }
  return opts;
}

// Rendu léger du texte IA : titres markdown/MAJUSCULES + gras + puces
function renderInline(text, keyBase) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) {
      return <strong key={`${keyBase}-${i}`} className="font-semibold text-[#0A192F]">{p.slice(2, -2)}</strong>;
    }
    return <span key={`${keyBase}-${i}`}>{p}</span>;
  });
}

function AiText({ text }) {
  const lines = text.split("\n");
  return (
    <div className="space-y-1.5">
      {lines.map((raw, i) => {
        let line = raw.trim();
        if (!line || line === "---" || /^[-*_]{3,}$/.test(line)) return <div key={i} className="h-1.5" />;

        const mdHeading = line.match(/^(#{1,6})\s+(.*)$/);
        const isUpperHeading =
          !mdHeading &&
          /^[A-ZÀ-Ÿ0-9'’ ,()\-&]+$/.test(line) &&
          line.length < 60 &&
          line === line.toUpperCase() &&
          /[A-ZÀ-Ÿ]/.test(line);

        if (mdHeading || isUpperHeading) {
          const content = mdHeading ? mdHeading[2].replace(/\*\*/g, "") : line;
          return (
            <h4 key={i} className="font-serif-title text-sm font-bold text-[#0A192F] uppercase tracking-wide mt-4 pb-1 border-b border-[#D4AF37]/40">
              {content}
            </h4>
          );
        }
        if (/^[-•*]\s+/.test(line)) {
          return (
            <div key={i} className="flex gap-2 text-sm text-slate-700 leading-relaxed">
              <span className="text-[#D4AF37] font-bold shrink-0">•</span>
              <span>{renderInline(line.replace(/^[-•*]\s*/, ""), i)}</span>
            </div>
          );
        }
        return <p key={i} className="text-sm text-slate-700 leading-relaxed">{renderInline(line, i)}</p>;
      })}
    </div>
  );
}

const StatBox = ({ icon: Icon, label, value, sub }) => (
  <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
    <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wide font-semibold mb-2">
      <Icon className="h-4 w-4 text-[#D4AF37]" /> {label}
    </div>
    <div className="font-serif-title text-2xl font-bold text-[#0A192F]">{value}</div>
    {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
  </div>
);

const TrendChip = ({ pct }) => {
  if (pct === null) return <span className="text-slate-400 text-xs">—</span>;
  const up = pct > 0, flat = pct === 0;
  const Icon = flat ? Minus : up ? TrendingUp : TrendingDown;
  const cls = flat ? "text-slate-500 bg-slate-100" : up ? "text-emerald-700 bg-emerald-50" : "text-red-700 bg-red-50";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${cls}`}>
      <Icon className="h-3 w-3" /> {up ? "+" : ""}{pct}%
    </span>
  );
};

const MonthlyComparison = ({ siteId }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    fetchTrends(6, siteId).then((d) => setData(d.months)).catch(() => setData([]));
  }, [siteId]);

  const withDelta = useMemo(() => {
    if (!data) return [];
    return data.map((m, i) => {
      const prev = i > 0 ? data[i - 1].total : null;
      let pct = null;
      if (prev !== null) {
        if (prev === 0) pct = m.total > 0 ? 100 : 0;
        else pct = Math.round(((m.total - prev) / prev) * 100);
      }
      return { ...m, pct };
    });
  }, [data]);

  if (!data) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 flex justify-center no-print">
        <Loader2 className="h-6 w-6 animate-spin text-[#0A192F]" />
      </div>
    );
  }

  const latest = withDelta[withDelta.length - 1];
  const totalAll = withDelta.reduce((s, m) => s + m.total, 0);

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 min-w-0" data-testid="monthly-comparison">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <h3 className="font-serif-title text-base font-bold text-[#0A192F]">
          Comparaison mensuelle — tendance d'affluence
        </h3>
        {latest && (
          <span className="text-xs text-slate-500 flex items-center gap-2 ml-auto">
            {latest.label} : <span className="font-semibold text-slate-700">{latest.total}</span> visites
            <TrendChip pct={latest.pct} /> <span className="hidden sm:inline">vs mois précédent</span>
          </span>
        )}
      </div>

      {totalAll === 0 ? (
        <p className="text-sm text-slate-400 py-10 text-center">Pas encore assez de données pour établir une tendance.</p>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={withDelta} margin={{ left: 0, right: 16, top: 16 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
              <Tooltip
                cursor={{ fill: "#f1f5f9" }}
                formatter={(v, n) => [v, n === "total" ? "Visites" : n]}
              />
              <Bar dataKey="total" radius={[4, 4, 0, 0]} name="total" maxBarSize={54}>
                {withDelta.map((_, i) => (
                  <Cell key={i} fill={MONTH_COLORS[i % MONTH_COLORS.length]} />
                ))}
                <LabelList dataKey="total" position="top" style={{ fontSize: 12, fill: "#334155" }} />
              </Bar>
              <Line type="monotone" dataKey="total" stroke="#D4AF37" strokeWidth={3} dot={{ r: 4, fill: "#D4AF37" }} name="tendance" />
            </ComposedChart>
          </ResponsiveContainer>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mt-4">
            {withDelta.map((m) => (
              <div key={m.month} className="rounded-lg border border-slate-200 p-2.5 text-center" data-testid={`trend-${m.month}`}>
                <div className="text-[11px] uppercase tracking-wide text-slate-500">{m.label}</div>
                <div className="font-serif-title text-lg font-bold text-[#0A192F]">{m.total}</div>
                <div className="mt-1"><TrendChip pct={m.pct} /></div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export const MonthlyReport = ({ sites = [], defaultSiteId = "" }) => {
  const options = useMemo(monthOptions, []);
  const [month, setMonth] = useState(options[0].value);
  const [siteId, setSiteId] = useState(defaultSiteId || "");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const siteName = sites.find((s) => s.id === siteId)?.name || "";

  const generate = async () => {
    setLoading(true);
    setReport(null);
    try {
      const data = await fetchMonthlyReport(month, siteId);
      setReport({ ...data, _siteName: siteName });
      if (!data.total) toast.info("Aucune visite enregistrée pour ce mois.");
      else toast.success("Rapport mensuel généré.");
    } catch {
      toast.error("Erreur lors de la génération du rapport.");
    } finally {
      setLoading(false);
    }
  };

  const csvUrl = () => {
    const [y, m] = month.split("-");
    const from = `${y}-${m}-01`;
    const last = new Date(parseInt(y), parseInt(m), 0).getDate();
    const to = `${y}-${m}-${String(last).padStart(2, "0")}`;
    const params = { period: "custom", frm: from, to };
    if (siteId) params.site_id = siteId;
    return exportCsvUrl(params);
  };

  const topServices = report ? report.services.slice(0, 8) : [];
  const maxHour = report ? Math.max(...report.hourly.map((h) => h.count), 0) : 0;

  return (
    <div className="space-y-6" data-testid="monthly-report">
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex flex-col sm:flex-row sm:items-center gap-3 no-print">
        <div className="flex items-center gap-2 text-[#0A192F] font-semibold">
          <FileText className="h-5 w-5 text-[#D4AF37]" /> Rapport mensuel — Direction
        </div>
        <div className="flex flex-wrap gap-3 sm:ml-auto">
          {sites.length > 0 && (
            <Select value={siteId || "__all__"} onValueChange={(v) => setSiteId(v === "__all__" ? "" : v)}>
              <SelectTrigger className="min-h-[46px] w-[200px] bg-slate-50 border-slate-300" data-testid="report-site-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__" data-testid="report-site-all">Tous les sites</SelectItem>
                {sites.map((s) => (
                  <SelectItem key={s.id} value={s.id} data-testid={`report-site-${s.id}`}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger className="min-h-[46px] w-[190px] bg-slate-50 border-slate-300" data-testid="month-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {options.map((o) => (
                <SelectItem key={o.value} value={o.value} data-testid={`month-${o.value}`}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            onClick={generate}
            disabled={loading}
            className="min-h-[46px] bg-[#0A192F] hover:bg-[#1E293B] text-white font-semibold border-b-2 border-[#D4AF37] gap-2"
            data-testid="generate-report-btn"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {loading ? "Génération IA…" : "Générer le rapport"}
          </Button>
          {report && report.total > 0 && (
            <>
              <a href={csvUrl()} data-testid="report-csv-btn">
                <Button variant="outline" className="min-h-[46px] border-slate-300 gap-2">
                  <Download className="h-4 w-4" /> CSV
                </Button>
              </a>
              <Button onClick={() => window.print()} className="min-h-[46px] bg-[#D4AF37] hover:bg-[#C59B27] text-[#0A192F] font-semibold gap-2" data-testid="report-print-btn">
                <Printer className="h-4 w-4" /> Imprimer
              </Button>
            </>
          )}
        </div>
      </div>

      <MonthlyComparison siteId={siteId} />

      {loading && (
        <div className="flex flex-col items-center justify-center py-24 text-slate-400 no-print">
          <Loader2 className="h-10 w-10 animate-spin text-[#0A192F] mb-3" />
          <p className="text-sm">Analyse des données et rédaction du rapport par l'IA…</p>
        </div>
      )}

      {report && report.total === 0 && !loading && (
        <div className="text-center py-20 text-slate-400 no-print" data-testid="report-empty">
          Aucune visite enregistrée pour {report.month_label}.
        </div>
      )}

      {report && report.total > 0 && (
        <div id="printable-monthly" className="space-y-6">
          {/* En-tête officiel (visible aussi à l'impression) */}
          <div className="flex items-center gap-4 bg-white rounded-xl border border-slate-200 shadow-sm p-5">
            <img src={logo} alt="SSG" className="h-16 w-16 object-contain rounded-md bg-white ring-2 ring-[#D4AF37]/50 p-1" />
            <div>
              <h2 className="font-serif-title text-xl font-bold text-[#0A192F]">RAPPORT MENSUEL DES VISITEURS</h2>
              <p className="text-sm text-[#8B6B1B] capitalize">SSG · Poste de Sécurité — {report.month_label}</p>
              <p className="text-sm font-semibold text-slate-700">Site : {report._siteName || "Tous les sites"}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatBox icon={FileText} label="Total visites" value={report.total} sub={`${report.active_days} jour(s) d'activité`} />
            <StatBox icon={Building2} label="Service n°1" value={report.services[0]?.name || "—"} sub={`${report.services[0]?.count || 0} visites`} />
            <StatBox icon={Clock} label="Heure de pointe" value={report.peak_hour.split(" ")[0]} sub={`${report.peak_hour_count} arrivées`} />
            <StatBox icon={CalendarDays} label="Durée moy." value={`${report.avg_duration_min} min`} sub={`Jour + chargé : ${report.busiest_day || "—"}`} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 min-w-0">
              <h3 className="font-serif-title text-base font-bold text-[#0A192F] mb-4">Services les plus fréquentés</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={topServices} layout="vertical" margin={{ left: 10, right: 20 }}>
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                  <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11 }} />
                  <Tooltip cursor={{ fill: "#f1f5f9" }} />
                  <Bar dataKey="count" radius={[0, 4, 4, 0]} name="Visites">
                    {topServices.map((_, i) => (
                      <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 min-w-0">
              <h3 className="font-serif-title text-base font-bold text-[#0A192F] mb-4">Affluence par heure de la journée</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={report.hourly} margin={{ left: 0, right: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="hour" tick={{ fontSize: 9 }} interval={1} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                  <Tooltip cursor={{ fill: "#f1f5f9" }} />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]} name="Arrivées">
                    {report.hourly.map((h, i) => (
                      <Cell key={i} fill={h.count === maxHour && maxHour > 0 ? "#D4AF37" : "#94a3b8"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {report.ai_summary ? (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6" data-testid="ai-summary">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="h-5 w-5 text-[#D4AF37]" />
                <h3 className="font-serif-title text-lg font-bold text-[#0A192F]">Analyse & recommandations</h3>
                <span className="text-[10px] uppercase tracking-wider text-slate-400 ml-auto no-print">Généré par IA · Claude</span>
              </div>
              <AiText text={report.ai_summary} />
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800 no-print">
              L'analyse IA n'a pas pu être générée. Les statistiques et graphiques restent disponibles.
            </div>
          )}

          <div className="hidden print:flex justify-between text-xs mt-10 pt-6">
            <div>Rapport établi par : _______________________</div>
            <div>Signature Direction : _______________________</div>
          </div>
        </div>
      )}

      {!report && !loading && (
        <div className="text-center py-20 text-slate-400 no-print">
          Sélectionnez un mois puis cliquez sur « Générer le rapport » pour obtenir une analyse complète assistée par IA.
        </div>
      )}
    </div>
  );
};
